import type { AccessTokenClaims } from '@fleetcore/shared'

/**
 * Token custody.
 *
 * The access token is held in module memory only. Putting a bearer credential in
 * localStorage means any injected script can read it and use it from anywhere;
 * keeping it in a closure at least confines theft to the lifetime of the page.
 *
 * The refresh token is persisted, because otherwise every hard reload signs the
 * user out — unacceptable on a console that people keep open all day. The
 * exposure is bounded by the server side of the contract: refresh tokens rotate
 * on every use and presenting a superseded generation revokes the whole family,
 * so a stolen refresh token is single-use and detectable.
 *
 * The end state is an httpOnly, SameSite=Strict cookie issued by the API, which
 * removes the persisted-token exposure entirely. See NOTES-FOR-INTEGRATOR.md —
 * this module is written so that switching costs one function.
 */

const REFRESH_TOKEN_KEY = 'fleetcore.rt'

let accessToken: string | null = null

type SessionEndedListener = (reason: 'expired' | 'revoked' | 'logout') => void
const sessionEndedListeners = new Set<SessionEndedListener>()

export function getAccessToken(): string | null {
  return accessToken
}

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
  } catch {
    return null
  }
}

export function setRefreshToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(REFRESH_TOKEN_KEY)
    else localStorage.setItem(REFRESH_TOKEN_KEY, token)
  } catch {
    /* Storage denied: the session then lasts exactly as long as this tab. */
  }
}

export function setTokens(tokens: { accessToken: string; refreshToken?: string | null }): void {
  setAccessToken(tokens.accessToken)
  if (tokens.refreshToken !== undefined) setRefreshToken(tokens.refreshToken)
}

export function clearTokens(): void {
  accessToken = null
  setRefreshToken(null)
}

export function onSessionEnded(listener: SessionEndedListener): () => void {
  sessionEndedListeners.add(listener)
  return () => sessionEndedListeners.delete(listener)
}

/** Called when the server refuses to renew: the auth store drops the user. */
export function notifySessionEnded(reason: 'expired' | 'revoked' | 'logout'): void {
  clearTokens()
  for (const listener of sessionEndedListeners) listener(reason)
}

/**
 * Reads the claims out of a JWT without verifying it.
 *
 * Safe only because nothing is authorized on the result: the API re-derives
 * tenant and roles from the signature on every request. It exists so the shell
 * can render the right navigation immediately after a reload instead of waiting
 * on a round-trip, and as a fallback when /auth/me is not deployed.
 */
export function decodeAccessTokenClaims(token: string): AccessTokenClaims | null {
  const payload = token.split('.')[1]
  if (!payload) return null

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const json = new TextDecoder().decode(
      Uint8Array.from(atob(padded), (character) => character.charCodeAt(0)),
    )
    const claims = JSON.parse(json) as Partial<AccessTokenClaims>
    if (typeof claims.sub !== 'string' || !Array.isArray(claims.roles)) return null
    return claims as AccessTokenClaims
  } catch {
    return null
  }
}

export const __testing = { REFRESH_TOKEN_KEY }
